class UiImage extends HTMLElement {
  constructor() {
    super();
    this.imageSrc = this.getAttribute('src') || '';
    this.altText = this.getAttribute('alt') || 'Hover image';
    this.imgHeight = this.getAttribute('height');
    this.imgWidth = this.getAttribute('width');
    this.hasImage = false;
    this.debounceUpdate = this.debounce(
      this.handleIntersectionVisibility.bind(this),
      200
    );
    this.sizes = this.getAttribute('sizes');
    this.srcset = this.getAttribute('srcset');
    this.imageClass = 'w-full h-full object-cover opacity-0';
    this.handleOnViewportDisconnect = null;
    this.createOnHover = false;
    this.createOnView = false;
    this.observer = null;
  }

  // Debounce utility function
  debounce(func, wait) {
    let timeout;

    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  connectedCallback() {
    if (this.createOnHover) {
      this.setupEventListeners();
    }

    this.style.position = 'relative';
    this.observer = new IntersectionObserver(
      this.handleIntersectionObserver.bind(this),
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 1.0], //trigger to 100% visibility
      }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.createOnView && this.observer) {
      this.observer.disconnect();
    }
  }

  setupEventListeners() {
    const parent = this.parentNode;
    if (parent && !this.hasImage) {
      parent.addEventListener('mouseenter', () => this.createImage(), {
        once: true,
      });
    }
  }

  createImage() {
    if (!this.hasImage) {
      this.hasImage = true;
      const img = document.createElement('img');
      img.src = this.imageSrc;
      img.alt = this.altText;
      img.width = this.imgWidth;
      img.height = this.imgHeight;
      img.className = this.imageClass;
      img.fetchPriority = 'auto';
      img.loading = 'lazy';
      img.sizes = this.sizes;
      img.srcset = this.srcset;
      // Append immediately to trigger load
      this.appendChild(img);

      img.addEventListener('load', () => {
        img.classList.add('opacity-100', 'bg-secondary4');
      });
    }
  }

  handleIntersectionObserver(entries) {
    entries.forEach((entry) => {
      this.debounceUpdate(entry); // calling the debounceUpdate at the constructor and pass the entry
    });
  }

  //This will be called in the debounceUpdate at the constructor
  handleIntersectionVisibility(entry) {
    const isVisible = entry.isIntersecting;
    const intersectionRatio = entry.intersectionRatio;
    const inRatio = intersectionRatio > 0.5;

    if (isVisible && inRatio) {
      this.createImage();
      this.observer.unobserve(entry.target);
    }
  }

  static get observedAttributes() {
    return ['src', 'alt'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'src') {
      this.imageSrc = newValue;
      if (this.hasImage) {
        const img = this.querySelector('img');
        if (img) {
          img.src = newValue;
          img.style.display = 'none'; // Hide until new src loads
          img.addEventListener('load', () => {
            img.style.display = 'block';
          });
          img.addEventListener('error', () => {
            console.error(`Failed to load new image: ${newValue}`);
            img.remove();
            this.hasImage = false;
          });
        }
      }
    } else if (name === 'alt') {
      this.altText = newValue;
      if (this.hasImage) {
        const img = this.querySelector('img');
        if (img) img.alt = newValue;
      }
    }
  }
}

customElements.define('ui-create-image', UiImage);
